export type CapacityInfo = { message: string; retryAt?: string };
// Thin typed client for the console backend. Same-origin in production; the
// Vite dev server proxies /api and /auth to the backend.

export type Me = { login: string | null; authorized: boolean; overlayAuthorized: boolean };

export type TargetFeatures = { overlayPins: boolean; forcePromote: boolean; redeploy: boolean };

/** One entry per enabled target the session is authorized for (see GET /api/targets).
 * `prodKind` mirrors the target's `ProdSpec['kind']` server-side: absent (an old
 * cached shape, or the bike4mind-only fallback) means 'statuses'. */
export type TargetInfo = { id: string; label: string; logo?: string; tenants: string[]; features: TargetFeatures; prodKind?: 'statuses' | 'deployments'; repo?: string; gateLabels?: { staging: string; tests: string; e2e?: string } };

export const DEFAULT_TARGET = 'bike4mind';

/** GitHub rate-limit state for a target, on a 503 or on a stale candidates snapshot. */
export type RateLimitInfo = { target: string; kind: 'primary' | 'secondary'; resource: string; resetAt: string };

/** Omit `target` for the default so every existing request stays byte-identical
 * when only bike4mind is enabled (the common case today). */
function scopedTarget(target?: string): string | undefined {
  return target && target !== DEFAULT_TARGET ? target : undefined;
}
function targetQuery(target?: string): string {
  const scoped = scopedTarget(target);
  return scoped ? `?target=${encodeURIComponent(scoped)}` : '';
}
function targetBody(target?: string): { target?: string } {
  const scoped = scopedTarget(target);
  return scoped ? { target: scoped } : {};
}

/** Where a Load more page starts: `after` is the oldest row shown, `offset` the
 * rows shown, `stopAt` the prod cut the page should end just below. */
export type CandidatePageQuery = { after: string; offset: number; stopAt?: string; mainline: boolean };

function candidatePageQuery(target: string | undefined, p: CandidatePageQuery): string {
  const q = new URLSearchParams({ after: p.after, offset: String(p.offset) });
  if (p.stopAt) q.set('stopAt', p.stopAt);
  if (p.mainline) q.set('mainline', '1');
  const scoped = scopedTarget(target);
  if (scoped) q.set('target', scoped);
  return `?${q}`;
}

// pending = in-progress/queued (amber), cancelled = superseded by a newer run
// (violet, distinct from both in-progress and not-run), missing = never ran (grey).
export type GateState = 'success' | 'failure' | 'pending' | 'cancelled' | 'skipped' | 'missing';

export type PromotionPolicySnapshot = { path: string; sourceCommitSha: string; blobSha: string; digest: string; requiredChecks: string[] };

export type Candidate = {
  policy?: PromotionPolicySnapshot;
  requiredChecks?: { name: string; state: GateState; url?: string }[];
  sha: string;
  subject: string;
  author: string;
  staging: boolean;
  tests: boolean;
  e2e?: boolean;
  stagingState?: GateState;
  testsState?: GateState;
  e2eState?: GateState;
  /** Actual production deploy outcome for this SHA; 'success' means it truly shipped. */
  prodState?: GateState;
  stagingUrl?: string;
  testsUrl?: string;
  e2eUrl?: string;
  prodUrl?: string;
  /** Set on a merge commit: how many commits its branch carried, none of which ran as main's tip. */
  branchCommits?: number;
};

/** One Load more page of older candidates (GET /api/candidates/more). */
export type CandidatePage = { candidates: Candidate[]; hasMore: boolean };

export type Maintainer = { login: string; avatarUrl: string; htmlUrl: string };

export type CommitInfo = {
  sha: string;
  subject: string;
  author: string;
  authorAvatarUrl?: string;
  authoredDate?: string;
  htmlUrl: string;
  pr?: number;
  /** Deploy-run URL for this SHA (production/deploy, falling back to staging/deploy). */
  runUrl?: string;
};

export type Status = 'pending' | 'dispatched' | 'rejected';

export type OverlayPinRow = { repo: string; ref: string; sha: string };

/** Per-overlay baseline ref vs the latest available SemVer tag (management panel). */
export type OverlayBaselineRow = { repo: string; ref: string; latest: string | null };

export type ReleaseRequest = {
  policy?: PromotionPolicySnapshot;
  id: string;
  sha: string;
  requester: string;
  approvers: string[];
  required: number;
  status: Status;
  createdAt?: string;
  runUrl?: string;
  forced?: boolean;
  /** Frozen overlay set (repo -> 40-char SHA) resolved at request creation. */
  overlayPins?: Record<string, string>;
  /** The ref each pin was resolved from (audit; 'main' unless overridden). */
  overlayRefs?: Record<string, string>;
  /** Promotion target this request was made against; absent means bike4mind. */
  target?: string;
  /** Frozen tenant scope for a target that declares `tenants` (e.g. polaris). */
  tenants?: string[];
};

/** Thrown by every failed API call. `.message` is unchanged; `code` is `body.error`
 * (e.g. 'GitHubRateLimited'). */
export class ApiError extends Error {
  status: number;
  code?: string;
  rateLimit?: RateLimitInfo;
  constructor(message: string, status: number, code?: string, rateLimit?: RateLimitInfo) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.rateLimit = rateLimit;
  }
}

export function isRateLimited(e: unknown): e is ApiError & { rateLimit: RateLimitInfo } {
  return e instanceof ApiError && e.code === 'GitHubRateLimited';
}

async function json<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body as any)?.message ?? (body as any)?.error ?? `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, (body as any)?.error, (body as any)?.rateLimit);
  }
  return body as T;
}

export const api = {
  me: () => fetch('/api/me').then(json<Me>),
  // Not target-scoped: only enabled targets the session is authorized for.
  // A session-less/anonymous caller gets a 401, which the app treats as [].
  targets: () => fetch('/api/targets').then(json<{ targets: TargetInfo[] }>).then(r => r.targets),
  candidates: (target?: string) =>
    fetch(`/api/candidates${targetQuery(target)}`).then(json<{
      candidates: Candidate[];
      prodAheadBy?: number;
      prodBeyondWindow?: boolean;
      mainline?: boolean;
      currentProdSha?: string;
      latestPromotion?: { sha: string; state: GateState };
      prodCandidate?: Candidate;
      /** deployments-kind targets only (e.g. polaris): current prod's own deploy
       * time (there may be no console request behind it to carry one), the
       * feq-production tenant's current SHA, and the sha to select to redeploy -
       * a normal, gate-checked candidate tagged with the prod sha it mirrors. */
      prodDeployedAt?: string;
      feqProdSha?: string;
      redeployTarget?: Candidate & { mirrorsProdSha: string };
      /** Set when this body is a cached snapshot served because GitHub is
       * currently rate limiting reads (see `rateLimit`), at most 2h old. */
      staleAsOf?: string;
      snapshotAsOf?: string;
      capacity?: CapacityInfo;
      rateLimit?: RateLimitInfo;
    }>),
  candidatesPage: (target: string | undefined, page: CandidatePageQuery) =>
    fetch(`/api/candidates/more${candidatePageQuery(target, page)}`).then(json<CandidatePage>),
  requests: (target?: string) =>
    fetch(`/api/requests${targetQuery(target)}`).then(json<{ requests: ReleaseRequest[] }>).then(r => r.requests),
  maintainers: (target?: string) =>
    fetch(`/api/maintainers${targetQuery(target)}`).then(json<{ maintainers: Maintainer[] }>).then(r => r.maintainers),
  commit: (sha: string, target?: string) =>
    fetch(`/api/commit/${sha}${targetQuery(target)}`).then(json<{ commit: CommitInfo | null }>).then(r => r.commit),
  overlayPins: (target?: string) =>
    fetch(`/api/overlay-pins${targetQuery(target)}`).then(json<{ enabled: boolean; pins: OverlayPinRow[] }>),
  overlayRefs: (repo: string, target?: string) =>
    fetch(`/api/overlay-refs/${encodeURIComponent(repo)}${targetQuery(target)}`).then(json<{ branches: string[]; tags: string[] }>),
  overlayBaseline: (target?: string) =>
    fetch(`/api/overlay-baseline${targetQuery(target)}`).then(json<{ enabled: boolean; overlays: OverlayBaselineRow[] }>),
  setOverlayBaseline: (repo: string, ref: string, target?: string) =>
    fetch('/api/overlay-baseline', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repo, ref, ...targetBody(target) }),
    }).then(json<{ ok: true; pins: Record<string, string>; commitSha: string }>),
  promote: (sha: string, force = false, overlayRefs?: Record<string, string>, target?: string, tenants?: string[]) =>
    fetch('/api/requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sha,
        force,
        ...(overlayRefs && Object.keys(overlayRefs).length ? { overlayRefs } : {}),
        ...(tenants && tenants.length ? { tenants } : {}),
        ...targetBody(target),
      }),
    }).then(json<{ request: ReleaseRequest }>).then(r => r.request),
  approve: (id: string, target?: string) =>
    fetch(`/api/requests/${id}/approve`, {
      method: 'POST',
      ...(target && target !== DEFAULT_TARGET
        ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(targetBody(target)) }
        : {}),
    }).then(json<{ request: ReleaseRequest }>).then(r => r.request),
};
